"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { type BatchLine, planBatch } from "@/lib/tool-kit/identifiers/batch";
import {
  type PubmedIdPlacement,
  pubmedArticleDraft,
  renderBibDrafts,
  withoutNlmQualifier,
} from "@/lib/tool-kit/identifiers/bibtex-entries";
import {
  inputLines,
  type PubmedId,
  parsePubmedId,
} from "@/lib/tool-kit/identifiers/identifier-input";
import {
  foldForSearch,
  type JournalRecord,
  journalsByTitle,
} from "@/lib/tool-kit/identifiers/journal-abbreviations";
import {
  type CiteEntry,
  citeLookup,
  type LookupFailure,
  loadJournalList,
} from "@/lib/tool-kit/identifiers/lookup";
import { useLookupRun } from "@/lib/tool-kit/identifiers/use-lookup-run";

/** English strings. A translated content file overrides these through `ui`. */
export const PMID_TO_BIBTEX_STRINGS = {
  inputLabel: "PMIDs or PMCIDs — one per line",
  inputPlaceholder:
    "29083010\nPMC5334499\nhttps://pubmed.ncbi.nlm.nih.gov/33301246/",
  inputHint:
    "Bare numbers, PMID: labels, PMC IDs and PubMed or PMC links all work. Up to {max} per run.",
  journalLabel: "Journal name",
  journalAbbr: "NLM abbreviation (Dalton Trans)",
  journalTitle: "Full title, as the NLM Catalog records it",
  placementLabel: "Where the PubMed ID goes",
  placementField: "pmid = {…} field (JabRef, BibDesk, Zotero)",
  placementNote: "note = {PMID: …} (printed by every style)",
  placementEprint: "eprint with eprinttype = {pubmed} (biblatex)",
  protectCapitals: "Brace capitalised title words so BibTeX keeps their case",
  asciiSafe: "ASCII-safe output — accents as LaTeX commands, e.g. {\\'e}",
  convert: "Convert to BibTeX",
  converting: "Looking up {done} of {total}…",
  emptyPrompt: "Paste at least one PMID or PMCID, then convert.",
  results: "BibTeX entries",
  entryCount: "one:{count} @article entry|other:{count} @article entries",
  copy: "Copy BibTeX",
  copied: "Copied!",
  download: "Download .bib",
  loadingJournals: "Loading the NLM journal list to abbreviate journal names…",
  journalsFailed:
    "The NLM journal list could not be loaded, so journal names are written in full.",
  journalsUnmatched:
    "one:{count} journal was not found in the NLM list and is written in full.|other:{count} journals were not found in the NLM list and are written in full.",
  statusHeading: "Lookup results",
  found: "Found",
  failed: "Not converted",
  notAnId:
    "Not a PubMed ID. A PMID is a number of up to eight digits; a PMCID is PMC followed by digits.",
  duplicate: "Same article as an earlier line — converted once.",
  overLimit: "Over the {max}-ID limit — run it in a second batch.",
  failNotFound:
    "PubMed returned no record for this ID. Check the number — a PMID and a PMCID are different numbers.",
  failRateLimited:
    "PubMed is rate-limiting requests. Wait a minute and try again.",
  failUnavailable:
    "The PubMed lookup did not answer properly. Try again shortly.",
  failTimeout: "The PubMed lookup took too long. Try again.",
  failNetwork: "The lookup could not be reached — check your connection.",
  noneConverted: "No ID could be converted — the reasons are listed below.",
  unexpected: "Something went wrong during the lookup. Please try again.",
} as const;

type Strings = typeof PMID_TO_BIBTEX_STRINGS;
type JournalMode = "abbr" | "title";
type JournalState = "idle" | "loading" | "ready" | "error";

const MAX_IDS = 25;
const BIB_MIME = "application/x-bibtex;charset=utf-8";

interface Row extends BatchLine<PubmedId | null> {
  entry?: CiteEntry;
  reason?: LookupFailure;
}

function planRows(text: string): Row[] {
  return planBatch(
    inputLines(text),
    parsePubmedId,
    (pubmed) => (pubmed ? `${pubmed.kind}:${pubmed.id}` : null),
    MAX_IDS
  );
}

/** What /api/cite reads: "pmid:29083010" or "PMC5334499". */
function lookupInput(pubmed: PubmedId): string {
  return pubmed.kind === "pmid" ? `pmid:${pubmed.id}` : pubmed.id;
}

function failureText(reason: LookupFailure | undefined, strings: Strings) {
  const messages: Record<LookupFailure, string> = {
    "not-found": strings.failNotFound,
    "rate-limited": strings.failRateLimited,
    unavailable: strings.failUnavailable,
    timeout: strings.failTimeout,
    network: strings.failNetwork,
    blocked: strings.failUnavailable,
  };
  return reason ? messages[reason] : strings.failUnavailable;
}

function rowDetail(row: Row, strings: Strings): string {
  switch (row.state) {
    case "invalid":
      return strings.notAnId;
    case "duplicate":
      return strings.duplicate;
    case "over":
      return t(strings.overLimit, { max: MAX_IDS });
    case "found":
      return row.entry?.title ?? "";
    default:
      return failureText(row.reason, strings);
  }
}

function StatusList({ rows, strings }: { rows: Row[]; strings: Strings }) {
  return (
    <ul className="space-y-2 text-sm">
      {rows.map((row) => {
        const ok = row.state === "found";
        return (
          <li
            className="rounded-lg border border-border bg-card p-3"
            key={row.id}
          >
            <span
              className={
                ok
                  ? "font-medium text-emerald-700 dark:text-emerald-400"
                  : "font-medium text-destructive"
              }
            >
              {ok ? strings.found : strings.failed}
            </span>{" "}
            <span className="break-all font-mono text-foreground">
              {row.parsed?.id ?? row.input}
            </span>
            <p className="mt-1 text-muted-foreground">
              {rowDetail(row, strings)}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

/** The journal name to write, and whether the NLM list had to be bypassed. */
function journalFor(
  entry: CiteEntry,
  mode: JournalMode,
  journals: Map<string, JournalRecord> | null
): { name: string; unmatched: boolean } {
  const title = entry.containerTitle ?? "";
  const full = withoutNlmQualifier(title);
  if (mode === "title" || !journals || title === "") {
    return { name: full, unmatched: false };
  }
  const record = journals.get(foldForSearch(title));
  return record?.medAbbr
    ? { name: record.medAbbr, unmatched: false }
    : { name: full, unmatched: true };
}

export default function PmidToBibtexWidget() {
  const strings = useToolUi(PMID_TO_BIBTEX_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [journalMode, setJournalMode] = useState<JournalMode>("abbr");
  const [placement, setPlacement] = useState<PubmedIdPlacement>("field");
  const [protect, setProtect] = useState(false);
  const [ascii, setAscii] = useState(false);
  const { rows, progress, failed, run } = useLookupRun<Row>();
  const [journals, setJournals] = useState<Map<string, JournalRecord> | null>(
    null
  );
  const [journalState, setJournalState] = useState<JournalState>("idle");

  const hasEntries = rows.some((row) => row.entry);
  useEffect(() => {
    if (journalMode !== "abbr" || !hasEntries || journalState !== "idle") {
      return;
    }
    setJournalState("loading");
    loadJournalList()
      .then((list) => {
        setJournals(journalsByTitle(list.records));
        setJournalState("ready");
      })
      .catch(() => setJournalState("error"));
  }, [journalMode, hasEntries, journalState]);

  function handleConvert() {
    if (journalState === "error") {
      setJournalState("idle");
    }
    run(
      planRows(text),
      (row, signal) => citeLookup(lookupInput(row.parsed as PubmedId), signal),
      (row, result): Row =>
        result.ok
          ? { ...row, state: "found", entry: result.value }
          : { ...row, state: "failed", reason: result.reason }
    );
  }

  const waitingForJournals =
    journalMode === "abbr" && journalState === "loading";
  const { output, unmatched } = useMemo(() => {
    const lookup = journalMode === "abbr" ? journals : null;
    let misses = 0;
    const drafts = rows.flatMap((row) => {
      if (!(row.entry && row.parsed)) {
        return [];
      }
      const journal = journalFor(row.entry, journalMode, lookup);
      misses += journal.unmatched ? 1 : 0;
      return [
        pubmedArticleDraft(row.entry, row.parsed, {
          protectCapitals: protect,
          asciiSafe: ascii,
          journal: journal.name,
          placement,
        }),
      ];
    });
    return { output: renderBibDrafts(drafts), unmatched: misses };
  }, [rows, journalMode, journals, placement, protect, ascii]);
  const found = rows.filter((row) => row.state === "found").length;
  const running = progress !== null;
  const finished = rows.length > 0 && !running;

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={t(strings.inputHint, { max: MAX_IDS })}
        id="p2b-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={6}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="p2b-journal"
          label={strings.journalLabel}
          onChange={setJournalMode}
          options={[
            { value: "abbr", label: strings.journalAbbr },
            { value: "title", label: strings.journalTitle },
          ]}
          value={journalMode}
        />
        <SelectField
          id="p2b-placement"
          label={strings.placementLabel}
          onChange={setPlacement}
          options={[
            { value: "field", label: strings.placementField },
            { value: "note", label: strings.placementNote },
            { value: "eprint", label: strings.placementEprint },
          ]}
          value={placement}
        />
      </div>
      <div className="space-y-2">
        <Checkbox
          checked={protect}
          label={strings.protectCapitals}
          onChange={setProtect}
        />
        <Checkbox
          checked={ascii}
          label={strings.asciiSafe}
          onChange={setAscii}
        />
      </div>
      <button
        className={BUTTON_PRIMARY}
        disabled={running || inputLines(text).length === 0}
        onClick={handleConvert}
        type="button"
      >
        {progress
          ? t(strings.converting, {
              done: fmt(progress.done),
              total: fmt(progress.total),
            })
          : strings.convert}
      </button>

      <div aria-live="polite" className="space-y-6">
        {failed ? <ErrorNotice>{strings.unexpected}</ErrorNotice> : null}
        {rows.length === 0 ? <Notice>{strings.emptyPrompt}</Notice> : null}
        {finished && waitingForJournals ? (
          <Notice>{strings.loadingJournals}</Notice>
        ) : null}
        {finished && found > 0 && !waitingForJournals ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className={WIDGET_H2}>{strings.results}</h2>
                <p className="text-muted-foreground text-sm">
                  {tPlural(strings.entryCount, locale, found, {
                    count: fmt(found),
                  })}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copy}
                  text={output}
                />
                <button
                  className={BUTTON_SECONDARY}
                  onClick={() =>
                    downloadText("references.bib", output, BIB_MIME)
                  }
                  type="button"
                >
                  {strings.download}
                </button>
              </div>
            </div>
            <pre className="max-h-96 overflow-auto rounded-lg border border-border bg-card p-4 font-mono text-foreground text-xs leading-relaxed">
              {output}
            </pre>
            {journalMode === "abbr" && journalState === "error" ? (
              <p className="text-muted-foreground text-xs">
                {strings.journalsFailed}
              </p>
            ) : null}
            {journalMode === "abbr" && unmatched > 0 ? (
              <p className="text-muted-foreground text-xs">
                {tPlural(strings.journalsUnmatched, locale, unmatched, {
                  count: fmt(unmatched),
                })}
              </p>
            ) : null}
          </div>
        ) : null}
        {finished && found === 0 ? (
          <ErrorNotice>{strings.noneConverted}</ErrorNotice>
        ) : null}
        {finished ? (
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.statusHeading}</h3>
            <StatusList rows={rows} strings={strings} />
          </div>
        ) : null}
      </div>
    </section>
  );
}
